import CardConfigToggleItem from '@/components/ReportDashboard/Shared/CardConfigToggleItem'
import { COMPANY_COLORS, getColorWithOpacity } from '@/constants/colors.constant'
import { RiskMetricId, isRiskMetricId } from '@/constants/ReportDashboard/api-enums'
import { CardTitleLookup } from '@/constants/ReportDashboard/cards-metadata'
import {
  usePortfolioMarketValue,
  useRiskMetricsSummary,
  useVaRParameters,
} from '@/contexts/RADashboardDataContext/hooks'
import { useDarkModeCheck, useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import {
  CardProps,
  RADashboardCardPrefs,
  RiskMetricsView,
  VaRPeriod,
  cardPrefsContextKey,
} from '@/interfaces/raDashboard.interface'
import { putUserPreference } from '@/services/userPreference.service'
import { buildCopyContextMenuItems, gridHeaderIconsSx, useClearCellRangeOnOutsideClick } from '@/components/ReportDashboard/Shared/GridActionsMenu'
import { getGridThemeClass } from '@/utils/grid-utils/ag-grid-config'
import { calculateCVaR, calculateVaR } from '@/utils/varCalcUtils'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import { ColDef, GridApi, GridReadyEvent, ICellRendererParams, ModuleRegistry } from '@ag-grid-community/core'
import { CsvExportModule } from '@ag-grid-community/csv-export'
import { AgGridReact } from '@ag-grid-community/react'
import { ClipboardModule } from '@ag-grid-enterprise/clipboard'
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export'
import { MenuModule } from '@ag-grid-enterprise/menu'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import TuneIcon from '@mui/icons-material/Tune'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { cardStyle } from '../../../styles/reportDashboardStyles'

ModuleRegistry.registerModules([ClientSideRowModelModule, CsvExportModule, ExcelExportModule, ClipboardModule, RangeSelectionModule, MenuModule])

interface RiskMetricsDisplayData {
  label: string
  description: string
  inverseChangePositive?: boolean // Whether a negative change value should be an improved metric
}

const COV_MATRIX_OPTIONS = ['GCV14 FI+EQ+Comm - Base', 'GCV14 FI+EQ+Comm - Exp.D = .94']

const VarPeriodLookup: Record<VaRPeriod, string> = {
  [VaRPeriod.Days]: 'Day(s)',
  [VaRPeriod.Months]: 'Month(s)',
  [VaRPeriod.Years]: 'Year(s)',
}
const VAR_PERIOD_UNITS = Object.values(VaRPeriod)

const formatTimePeriod = (value: number, unit: string) => `${value} ${unit}`

const DEFAULT_VIEW: RiskMetricsView = {
  id: 'primary',
  name: 'Primary View',
  covarianceMatrix: COV_MATRIX_OPTIONS[0], // 'GCV14 FI+EQ+Comm - Base'
  varConfidenceLevel: 95,
  varTimePeriodValue: 1,
  varTimePeriodUnit: VaRPeriod.Days,
}

const MetricsDisplayInfo: Record<RiskMetricId, RiskMetricsDisplayData> = {
  [RiskMetricId.TotalRiskPortfolio]: {
    label: 'Portfolio Total Risk',
    description: 'Portfolio Annualized Standard Deviation',
  },
  [RiskMetricId.TotalRiskBenchmark]: {
    label: 'Benchmark Total Risk',
    description: 'Benchmark Annualized Standard Deviation',
  },
  [RiskMetricId.TotalRiskActive]: {
    label: 'Tracking Error',
    description: 'vs. Benchmark',
  },
  [RiskMetricId.VaR]: {
    label: 'VaR',
    description: 'Value at Risk',
  },
  [RiskMetricId.CVaR]: {
    label: 'CVaR',
    description: 'Conditional VaR (Expected Shortfall)',
  },
}
const MetricsEnumerable = Object.entries(MetricsDisplayInfo)

function calculateDelta(curr: number | null, prev: number | null) {
  if (curr === null || prev === null || prev === 0) return null
  return (curr / prev - 1) * 100
}

// ag-grid renderer for the Change column — colored ↑/↓ delta with a tooltip.
const ChangeCellRenderer = (params: ICellRendererParams) => {
  const theme = useTheme()
  const change = params.value as number | null
  const deltaDate = params.data?.deltaDate as Date | null
  if (!change) {
    return <span style={{ color: theme.textSecondary }}>– –</span>
  }
  const changePositive = change > 0
  const deltaFormatter = Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    signDisplay: 'always',
  })
  return (
    <Tooltip
      title={`${changePositive ? 'Up' : 'Down'} from ${deltaDate ? dayjs(deltaDate).format('MM-DD-YYYY') : '???'}`}
    >
      <span
        style={{
          fontWeight: 600,
          color: changePositive ? COMPANY_COLORS.SUCCESS : COMPANY_COLORS.ERROR,
          whiteSpace: 'nowrap',
        }}
      >
        {changePositive ? '↑' : '↓'} {deltaFormatter.format(change / 100)}
      </span>
    </Tooltip>
  )
}

const PRIMARY_VIEW_ID = 'primary'
const RiskMetricsSummaryCard: React.FC<CardProps> = (props) => {
  const theme = useTheme()
  const isDarkMode = useDarkModeCheck()
  const gridContextMenuItems = useMemo(
    () => buildCopyContextMenuItems({ processCell: (p) => (p.column?.getColId?.() === 'value' ? p.node?.data?.rawValue : p.value) }),
    [],
  )

  const marketValue = usePortfolioMarketValue()
  const varParams = useVaRParameters()
  const riskMetricsData = useRiskMetricsSummary()
  const portfolioRisk = riskMetricsData?.[RiskMetricId.TotalRiskPortfolio] ?? null
  const prevAnalysisDate = riskMetricsData?.previousReportData?.analysisDate ?? null
  const prevRisk = riskMetricsData?.previousReportData?.portfolioRisk ?? null
  const prevMarketValue = riskMetricsData?.previousReportData?.marketValue ?? null

  const PRIMARY_VIEW: RiskMetricsView =
    varParams == null
      ? DEFAULT_VIEW
      : {
          ...DEFAULT_VIEW,
          varConfidenceLevel: varParams.rate,
          varTimePeriodValue: varParams.length,
          varTimePeriodUnit: varParams.period,
        }

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [visibleMetricIds, setVisibleMetricIds] = useState<Set<RiskMetricId>>(
    () => new Set(props.cardPrefs?.riskMetricsSummary?.visibleMetricIds ?? Object.values(RiskMetricId)),
  )

  // Only user-created views are stored. The Primary view is derived from the
  // report's live VaR parameters and prepended on each render, so it always
  // reflects the current report (varParams loads asynchronously after prefs).
  const [customViews, setCustomViews] = useState<RiskMetricsView[]>(
    () => props.cardPrefs?.riskMetricsSummary?.views ?? [],
  )
  const [activeViewId, setActiveViewId] = useState<string>(
    props.cardPrefs?.riskMetricsSummary?.activeViewId ?? PRIMARY_VIEW_ID,
  )

  const views = [PRIMARY_VIEW, ...customViews]

  useEffect(() => {
    const summary = props.cardPrefs?.riskMetricsSummary
    if (summary?.visibleMetricIds) {
      setVisibleMetricIds(new Set(summary.visibleMetricIds))
    }
    if (summary?.views) {
      setCustomViews(summary.views)
    }
    if (summary?.activeViewId) {
      setActiveViewId(summary.activeViewId)
    }
  }, [props.cardPrefs])

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [newViewName, setNewViewName] = useState('')
  const [newCovMatrix, setNewCovMatrix] = useState(DEFAULT_VIEW.covarianceMatrix)
  const [newVarConfidence, setNewVarConfidence] = useState(varParams?.rate ?? DEFAULT_VIEW.varConfidenceLevel)
  const [newVarPeriodValue, setNewVarPeriodValue] = useState(varParams?.length ?? DEFAULT_VIEW.varTimePeriodValue)
  const [newVarPeriodUnit, setNewVarPeriodUnit] = useState(varParams?.period ?? DEFAULT_VIEW.varTimePeriodUnit)

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingView, setEditingView] = useState<RiskMetricsView | null>(null)

  const activeView = views.find((v) => v.id === activeViewId) ?? views[0]

  const varParamString = `(${activeView.varConfidenceLevel}%, ${activeView.varTimePeriodValue}${activeView.varTimePeriodUnit[0]})`
  const currencyFormatter = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    notation: 'compact',
  })
  const decimalFormatter = Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  const viewVar = useMemo(() => {
    if (portfolioRisk === null || marketValue === null) {
      return null
    }
    const varConfidence = activeView.varConfidenceLevel
    const varTime = activeView.varTimePeriodValue
    const varPeriod = activeView.varTimePeriodUnit
    return calculateVaR(portfolioRisk.value, marketValue, varConfidence, varTime, varPeriod)
  }, [activeView, portfolioRisk, marketValue])
  const prevVar = useMemo(() => {
    if (prevRisk === null || prevMarketValue === null) {
      return null
    }
    const varConfidence = activeView.varConfidenceLevel
    const varTime = activeView.varTimePeriodValue
    const varPeriod = activeView.varTimePeriodUnit
    return calculateVaR(prevRisk, prevMarketValue, varConfidence, varTime, varPeriod)
  }, [activeView, prevRisk, prevMarketValue])
  const viewCVar = useMemo(() => {
    if (portfolioRisk === null || marketValue === null) {
      return null
    }
    const varConfidence = activeView.varConfidenceLevel
    const varTime = activeView.varTimePeriodValue
    const varPeriod = activeView.varTimePeriodUnit
    return calculateCVaR(portfolioRisk.value, marketValue, varConfidence, varTime, varPeriod)
  }, [activeView, portfolioRisk, marketValue])
  const prevCVar = useMemo(() => {
    if (prevRisk === null || prevMarketValue === null) {
      return null
    }
    const varConfidence = activeView.varConfidenceLevel
    const varTime = activeView.varTimePeriodValue
    const varPeriod = activeView.varTimePeriodUnit
    return calculateCVaR(prevRisk, prevMarketValue, varConfidence, varTime, varPeriod)
  }, [activeView, prevRisk, prevMarketValue])

  const gridRef = useRef<AgGridReact>(null)
  const gridBoxRef = useRef<HTMLDivElement>(null)
  useClearCellRangeOnOutsideClick(gridBoxRef, () => gridRef.current?.api)

  // Rows for the ag-grid summary table (also drives CSV / Excel export).
  const rowData = useMemo(() => {
    if (!riskMetricsData) return []
    const rows: {
      metric: string
      value: string
      rawValue: number
      description: string
      change: number | null
      deltaDate: Date | null
    }[] = []
    MetricsEnumerable.forEach(([id, displayInfo]) => {
      if (!isRiskMetricId(id) || !visibleMetricIds.has(id)) return
      let label = displayInfo.label
      const isVar = id === RiskMetricId.VaR || id === RiskMetricId.CVaR
      let numValue: number
      let numDelta: number | null = null
      let value: string
      if (isVar) {
        let prevValue: number | null = null
        if (id === RiskMetricId.VaR) {
          numValue = viewVar ?? 0
          prevValue = prevVar
        } else if (id === RiskMetricId.CVaR) {
          numValue = viewCVar ?? 0
          prevValue = prevCVar
        } else {
          numValue = 0
        }
        numDelta = calculateDelta(numValue, prevValue)
        label += ' ' + varParamString
        value = currencyFormatter.format(numValue)
      } else {
        numValue = riskMetricsData[id].value
        numDelta = riskMetricsData[id].delta
        value = decimalFormatter.format(numValue) + '%'
      }
      rows.push({
        metric: label,
        value,
        rawValue: numValue,
        description: displayInfo.description,
        change: numDelta,
        deltaDate: prevAnalysisDate,
      })
    })
    return rows
  }, [
    riskMetricsData,
    visibleMetricIds,
    viewVar,
    viewCVar,
    prevVar,
    prevCVar,
    varParamString,
    prevAnalysisDate,
    currencyFormatter,
    decimalFormatter,
  ])

  // One ag-grid API per view tab — drives the multi-sheet Excel export.
  const exportApis = useRef<Record<string, GridApi>>({})

  // Rows for an arbitrary view tab (VaR/CVaR depend on the view's params; the
  // other metrics are identical across views). Mirrors the active-view rowData.
  const buildRows = useCallback(
    (view: RiskMetricsView) => {
      type Row = {
        metric: string
        value: string
        rawValue: number
        description: string
        change: number | null
        deltaDate: Date | null
      }
      if (!riskMetricsData) return [] as Row[]
      const { varConfidenceLevel: vConf, varTimePeriodValue: vTime, varTimePeriodUnit: vUnit } = view
      const varParamStr = `(${vConf}%, ${vTime}${vUnit[0]})`
      const vVar =
        portfolioRisk !== null && marketValue !== null
          ? calculateVaR(portfolioRisk.value, marketValue, vConf, vTime, vUnit)
          : null
      const pVar =
        prevRisk !== null && prevMarketValue !== null
          ? calculateVaR(prevRisk, prevMarketValue, vConf, vTime, vUnit)
          : null
      const vCVar =
        portfolioRisk !== null && marketValue !== null
          ? calculateCVaR(portfolioRisk.value, marketValue, vConf, vTime, vUnit)
          : null
      const pCVar =
        prevRisk !== null && prevMarketValue !== null
          ? calculateCVaR(prevRisk, prevMarketValue, vConf, vTime, vUnit)
          : null
      const rows: Row[] = []
      MetricsEnumerable.forEach(([id, displayInfo]) => {
        if (!isRiskMetricId(id) || !visibleMetricIds.has(id)) return
        let label = displayInfo.label
        const isVar = id === RiskMetricId.VaR || id === RiskMetricId.CVaR
        let numValue: number
        let numDelta: number | null = null
        let value: string
        if (isVar) {
          let prevValue: number | null = null
          if (id === RiskMetricId.VaR) {
            numValue = vVar ?? 0
            prevValue = pVar
          } else if (id === RiskMetricId.CVaR) {
            numValue = vCVar ?? 0
            prevValue = pCVar
          } else {
            numValue = 0
          }
          numDelta = calculateDelta(numValue, prevValue)
          label += ' ' + varParamStr
          value = currencyFormatter.format(numValue)
        } else {
          numValue = riskMetricsData[id].value
          numDelta = riskMetricsData[id].delta
          value = decimalFormatter.format(numValue) + '%'
        }
        rows.push({
          metric: label,
          value,
          rawValue: numValue,
          description: displayInfo.description,
          change: numDelta,
          deltaDate: prevAnalysisDate,
        })
      })
      return rows
    },
    [
      riskMetricsData,
      visibleMetricIds,
      portfolioRisk,
      marketValue,
      prevRisk,
      prevMarketValue,
      prevAnalysisDate,
      currencyFormatter,
      decimalFormatter,
    ],
  )

  const columnDefs = useMemo<ColDef[]>(
    () =>
      [
        { headerName: 'Metric', field: 'metric', flex: 1.2, minWidth: 140, cellStyle: { fontWeight: 700 } },
        {
          headerName: 'Value',
          field: 'value',
          flex: 0.6,
          minWidth: 90,
          cellClass: 'ag-right-aligned-cell',
          cellStyle: { fontWeight: 700 },
        },
        {
          headerName: 'Description',
          field: 'description',
          flex: 2,
          minWidth: 180,
          cellStyle: { color: theme.textSecondary },
        },
        {
          headerName: 'Change',
          field: 'change',
          flex: 0.6,
          minWidth: 100,
          cellClass: 'ag-right-aligned-cell',
          cellRenderer: ChangeCellRenderer,
        },
      ] as ColDef[],
    [theme],
  )

  const defaultColDef = useMemo<ColDef>(() => ({ sortable: true, filter: false, resizable: true }), [])

  const handleExport = (type: 'CSV' | 'EXCEL') => {
    // Export the raw numeric Value (full precision)
    const processCellCallback = (p: any) => (p.column?.getColId?.() === 'value' ? p.node?.data?.rawValue : p.value)
    if (type === 'EXCEL') {
      // One sheet per view tab, named by the tab name (sanitized for Excel).
      const sanitize = (n: string) => (n || 'Sheet').replace(/[:\\/?*[\]]/g, ' ').slice(0, 31)
      const sheets = views
        .map((v) =>
          exportApis.current[v.id]?.getSheetDataForExcel({ sheetName: sanitize(v.name), processCellCallback }),
        )
        .filter((s): s is string => !!s)
      const anyApi = exportApis.current[activeViewId] ?? Object.values(exportApis.current)[0] ?? gridRef.current?.api
      if (anyApi && sheets.length) anyApi.exportMultipleSheetsAsExcel({ data: sheets, fileName: 'risk_metric_summary' })
    } else {
      // One CSV with each view tab as a labelled section: a tab-name header row,
      // the tab's table, then a blank line before the next tab.
      const parts = views
        .map((v) => {
          const csv = exportApis.current[v.id]?.getDataAsCsv({ processCellCallback })
          return csv ? `${v.name}\n${csv}` : null
        })
        .filter((p): p is string => !!p)
      if (parts.length) {
        const blob = new Blob(['﻿' + parts.join('\n\n')], {
          type: 'text/csv;charset=utf-8;',
        })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'risk_metric_summary.csv'
        a.click()
        URL.revokeObjectURL(url)
      }
    }
    setMenuAnchor(null)
  }

  // Persist the card's risk-metrics preferences. The Primary view is derived
  // from the report's VaR parameters, so only user-created views are saved.
  const writePrefs = (next: {
    views?: RiskMetricsView[]
    visibleMetricIds?: Set<RiskMetricId>
    activeViewId?: string
  }) => {
    if (!props.cardPrefs?.dashboardName) return
    const nextViews = next.views ?? customViews
    const newPrefs: RADashboardCardPrefs = {
      ...props.cardPrefs,
      riskMetricsSummary: {
        visibleMetricIds: [...(next.visibleMetricIds ?? visibleMetricIds)],
        views: nextViews.filter((v) => v.id !== PRIMARY_VIEW_ID),
        activeViewId: next.activeViewId ?? activeViewId,
      },
    }
    props.onCardPrefsChange?.(newPrefs)
    putUserPreference(cardPrefsContextKey(props.cardPrefs.dashboardName), newPrefs)
  }

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => setMenuAnchor(e.currentTarget)

  const handleMenuClose = () => {
    setMenuAnchor(null)
    writePrefs({})
  }

  const toggleMetricRow = (metricId: RiskMetricId) => {
    const next = new Set(visibleMetricIds)
    next.has(metricId) ? next.delete(metricId) : next.add(metricId)
    setVisibleMetricIds(next)
    writePrefs({ visibleMetricIds: next })
  }

  const handleSelectView = (viewId: string) => {
    setActiveViewId(viewId)
    writePrefs({ activeViewId: viewId })
  }

  const openCreateDialog = () => {
    setNewViewName('')
    setNewCovMatrix(COV_MATRIX_OPTIONS[0])
    setNewVarConfidence(95)
    setNewVarPeriodValue(1)
    setNewVarPeriodUnit(VaRPeriod.Days)
    setCreateDialogOpen(true)
  }

  const handleCreateView = () => {
    const id = `view_${Date.now()}`
    const newView: RiskMetricsView = {
      id,
      name: newViewName.trim() || `View ${views.length + 1}`,
      covarianceMatrix: newCovMatrix,
      varConfidenceLevel: newVarConfidence,
      varTimePeriodValue: newVarPeriodValue,
      varTimePeriodUnit: newVarPeriodUnit,
    }
    const nextViews = [...customViews, newView]
    setCustomViews(nextViews)
    setActiveViewId(id)
    setCreateDialogOpen(false)
    writePrefs({ views: nextViews, activeViewId: id })
  }

  const openEditDialog = (e: React.MouseEvent, view: RiskMetricsView) => {
    e.stopPropagation()
    setEditingView({ ...view })
    setEditDialogOpen(true)
  }

  const handleSaveEdit = () => {
    if (!editingView) return
    const nextViews = customViews.map((v) => (v.id === editingView.id ? editingView : v))
    setCustomViews(nextViews)
    setEditDialogOpen(false)
    setEditingView(null)
    writePrefs({ views: nextViews })
  }

  const handleDeleteView = (e: React.MouseEvent, viewId: string) => {
    e.stopPropagation()
    const nextViews = customViews.filter((v) => v.id !== viewId)
    setCustomViews(nextViews)
    const nextActiveId = activeViewId === viewId ? PRIMARY_VIEW_ID : activeViewId
    if (activeViewId === viewId) {
      setActiveViewId(PRIMARY_VIEW_ID)
    }
    writePrefs({ views: nextViews, activeViewId: nextActiveId })
  }

  const viewSubtitle = `${activeView.covarianceMatrix} • ${activeView.varConfidenceLevel}% VaR • ${formatTimePeriod(activeView.varTimePeriodValue, activeView.varTimePeriodUnit)}`

  const cardLayout = {
    width: '100%',
    height: '100%',
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
    padding: '16px',
    boxSizing: 'border-box',
  }

  const headerStyle = {
    paddingBottom: '8px',
    marginBottom: '12px',
  }

  const subtitleStyle = {
    fontSize: '14px',
    color: theme.textSecondary,
    marginTop: '2px',
  }

  const dialogInputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.inputBackground,
      color: theme.textPrimary,
      fontSize: '14px',
      '& fieldset': { borderColor: theme.border },
      '&:hover fieldset': { borderColor: theme.textSecondary },
      '&.Mui-focused fieldset': { borderColor: theme.textPrimary },
    },
    '& .MuiInputLabel-root': { color: theme.textPrimary, fontWeight: 600 },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.textPrimary,
      fontWeight: 600,
    },
    '& .MuiFormHelperText-root': { color: theme.textSecondary },
  }

  const dialogSx = {
    backgroundColor: theme.cardBackground,
    border: `1px solid ${theme.border}`,
    borderRadius: '8px',
    color: theme.textPrimary,
  }

  const dialogActionsSx = {
    color: theme.textPrimary,
    borderColor: theme.border,
    textTransform: 'none' as const,
    '&:hover': { borderColor: theme.textSecondary },
  }

  const dialogPrimaryBtn = {
    backgroundColor: theme.textPrimary,
    color: theme.cardBackground,
    textTransform: 'none' as const,
    fontWeight: 600,
    '&:hover': { backgroundColor: theme.textSecondary },
  }

  return (
    <Stack
      sx={{
        ...cardStyle,
        ...cardLayout,
        backgroundColor: theme.cardBackground,
        border: `1px solid ${theme.border}`,
      }}
    >
      {/* Header */}
      <Stack sx={headerStyle} direction='row' justifyContent='space-between' alignItems='flex-start'>
        <Box>
          <Typography variant='subtitle2' sx={{ fontWeight: 700, color: theme.textPrimary }}>
            {CardTitleLookup[props.cardId]}
          </Typography>
          <Typography sx={subtitleStyle}>{viewSubtitle}</Typography>
        </Box>
        <IconButton size='small' sx={{ color: theme.textSecondary }} onClick={handleMenuOpen}>
          <MoreVertIcon fontSize='small' />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              backgroundColor: theme.cardBackground,
              border: `1px solid ${theme.border}`,
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              minWidth: 200,
            },
          }}
        >
          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: theme.textPrimary, px: 2, py: 1 }}>
            Configure Metrics
          </Typography>
          {MetricsEnumerable.map(([metricId, metricInfo]) => {
            if (!isRiskMetricId(metricId)) return false
            return (
              <CardConfigToggleItem
                key={metricId}
                label={metricInfo.label}
                isVisible={visibleMetricIds.has(metricId)}
                onToggle={() => toggleMetricRow(metricId)}
              />
            )
          })}
          <Divider sx={{ borderColor: theme.border, my: 0.5 }} />
          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: theme.textPrimary, px: 2, py: 1 }}>
            Export
          </Typography>
          <MenuItem
            onClick={() => handleExport('EXCEL')}
            sx={{ fontSize: '14px', color: theme.textPrimary, '&:hover': { backgroundColor: theme.border } }}
          >
            Export as Excel
          </MenuItem>
          <MenuItem
            onClick={() => handleExport('CSV')}
            sx={{ fontSize: '14px', color: theme.textPrimary, '&:hover': { backgroundColor: theme.border } }}
          >
            Export as CSV
          </MenuItem>
        </Menu>
      </Stack>

      {/* View Tabs */}
      <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: '24px', minWidth: 0, width: '100%' }}>
        {/* Tab pill container — scrolls horizontally when views overflow */}
        <Stack
          direction='row'
          alignItems='center'
          sx={{
            border: `1px solid ${theme.border}`,
            borderRadius: '8px',
            padding: '3px',
            gap: '2px',
            backgroundColor: theme.cardBackground,
            minWidth: 0,
            overflowX: 'auto',
            '&::-webkit-scrollbar': { height: '6px' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.border,
              borderRadius: '3px',
            },
          }}
        >
          {views.map((view) => {
            const isActive = view.id === activeViewId
            const isPrimary = view.id === PRIMARY_VIEW_ID
            return (
              <Stack
                key={view.id}
                direction='row'
                alignItems='center'
                onClick={() => handleSelectView(view.id)}
                sx={{
                  px: '10px',
                  py: '4px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  gap: '4px',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  backgroundColor: isActive ? theme.cardBackground : 'transparent',
                  boxShadow: isActive ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
                  border: isActive ? `1px solid ${theme.border}` : '1px solid transparent',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    backgroundColor: isActive ? theme.cardBackground : theme.border + '55',
                  },
                  '&:hover .tab-actions': { opacity: 1 },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? theme.textPrimary : theme.textSecondary,
                    userSelect: 'none',
                    lineHeight: 1,
                  }}
                >
                  {view.name}
                </Typography>

                {/* Edit / Delete icons — visible only on tab hover */}
                {!isPrimary && (
                  <Stack direction='row' className='tab-actions' sx={{ opacity: 0, transition: 'opacity 0.15s ease' }}>
                    <IconButton
                      size='small'
                      onClick={(e) => openEditDialog(e, view)}
                      sx={{
                        padding: '2px',
                        color: theme.textSecondary,
                        '&:hover': { color: theme.textPrimary },
                      }}
                    >
                      <TuneIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                    <IconButton
                      size='small'
                      onClick={(e) => handleDeleteView(e, view.id)}
                      sx={{
                        padding: '2px',
                        color: theme.textSecondary,
                        '&:hover': { color: COMPANY_COLORS.ERROR },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Stack>
                )}
              </Stack>
            )
          })}
        </Stack>

        {/* Add tab button — sits outside the pill container */}
        <IconButton
          size='small'
          onClick={openCreateDialog}
          sx={{
            color: theme.textSecondary,
            border: `1px solid ${theme.border}`,
            borderRadius: '8px',
            width: 28,
            height: 28,
            flexShrink: 0,
            '&:hover': {
              color: theme.textPrimary,
              borderColor: theme.textSecondary,
            },
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>

      {/* Table — separate bordered section (ag-grid) */}
      <Box
        ref={gridBoxRef}
        className={getGridThemeClass(isDarkMode)}
        sx={[
          {
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            border: `1px solid ${theme.border}`,
            borderRadius: '8px',
            // cwan theme drives colors/borders/fonts; keep only grid-specific gaps.
            '--ag-secondary-foreground-color': theme.textSecondary,
            '--ag-row-hover-color': getColorWithOpacity(theme.border, 0.4),
          } as React.CSSProperties,
          gridHeaderIconsSx,
        ]}
      >
        <AgGridReact
          ref={gridRef}
          containerStyle={{ height: '100%', width: '100%' }}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          animateRows={false}
          columnHoverHighlight
          enableRangeSelection
          popupParent={document.body}
          getContextMenuItems={gridContextMenuItems}
          processCellForClipboard={(p: any) =>
            p.column?.getColId?.() === 'value' ? String(p.node?.data?.rawValue ?? p.value ?? '') : p.value
          }
        />
      </Box>

      {/* Hidden per-view grids — one Excel sheet per view tab on export */}
      <Box sx={{ display: 'none' }} aria-hidden>
        {views.map((v) => (
          <AgGridReact
            key={v.id}
            rowData={buildRows(v)}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onGridReady={(e: GridReadyEvent) => {
              exportApis.current[v.id] = e.api
            }}
          />
        ))}
      </Box>

      {/* Create New View Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth='xs'
        fullWidth
        PaperProps={{ sx: dialogSx }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '16px', color: theme.textPrimary }}>
            Create New Risk Metrics View
          </Typography>
          <Typography sx={{ fontSize: '14px', color: theme.textSecondary, mt: '2px' }}>
            Add a new tab to organize different risk metric configurations
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            pt: '16px !important',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <TextField
            label='View Name'
            placeholder='e.g., VaR Analysis, Tracking Error Focus...'
            InputLabelProps={{ shrink: true }}
            value={newViewName}
            onChange={(e) => setNewViewName(e.target.value)}
            fullWidth
            size='small'
            sx={dialogInputSx}
          />
          <FormControl fullWidth size='small' sx={dialogInputSx}>
            <InputLabel>Covariance Matrix</InputLabel>
            <Select
              label='Covariance Matrix'
              value={newCovMatrix}
              onChange={(e) => setNewCovMatrix(e.target.value)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: theme.cardBackground,
                    border: `1px solid ${theme.border}`,
                    color: theme.textPrimary,
                  },
                },
              }}
            >
              {COV_MATRIX_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt} sx={{ color: theme.textPrimary, fontSize: '14px' }}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <TextField
              label='VaR Confidence Level (%)'
              type='number'
              value={newVarConfidence}
              onChange={(e) => setNewVarConfidence(Number(e.target.value))}
              inputProps={{ min: 0, max: 100, step: 0.5 }}
              size='small'
              sx={dialogInputSx}
            />
            <FormHelperText sx={{ color: theme.textSecondary, mx: 0 }}>
              Enter any value between 0-100 (e.g., 90, 95, 97.5, 99)
            </FormHelperText>
          </FormControl>
          <Box>
            <Typography
              sx={{
                fontSize: '14px',
                color: theme.textPrimary,
                fontWeight: 600,
                mb: '6px',
              }}
            >
              VaR Time Period
            </Typography>
            <Stack direction='row' spacing={1.5}>
              <TextField
                type='number'
                value={newVarPeriodValue}
                onChange={(e) => setNewVarPeriodValue(Number(e.target.value))}
                inputProps={{ min: 1, step: 1 }}
                size='small'
                sx={{ ...dialogInputSx, flex: 1 }}
              />
              <FormControl size='small' sx={{ ...dialogInputSx, flex: 2 }}>
                <InputLabel>Unit</InputLabel>
                <Select
                  label='Unit'
                  value={newVarPeriodUnit}
                  onChange={(e) => setNewVarPeriodUnit(e.target.value as VaRPeriod)}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: theme.cardBackground,
                        border: `1px solid ${theme.border}`,
                        color: theme.textPrimary,
                      },
                    },
                  }}
                >
                  <MenuItem value='' disabled sx={{ color: theme.textSecondary, fontSize: '14px' }}>
                    Select
                  </MenuItem>
                  {VAR_PERIOD_UNITS.map((u) => (
                    <MenuItem key={u} value={u} sx={{ color: theme.textPrimary, fontSize: '14px' }}>
                      {VarPeriodLookup[u]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button variant='outlined' onClick={() => setCreateDialogOpen(false)} sx={dialogActionsSx}>
            Cancel
          </Button>
          <Button variant='contained' onClick={handleCreateView} sx={dialogPrimaryBtn}>
            Create View
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit View Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth='xs'
        fullWidth
        PaperProps={{ sx: dialogSx }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '16px', color: theme.textPrimary }}>
            Edit Risk Metrics View
          </Typography>
          <Typography sx={{ fontSize: '14px', color: theme.textSecondary, mt: '2px' }}>
            Update the configuration for this view
          </Typography>
        </DialogTitle>
        {editingView && (
          <DialogContent
            sx={{
              pt: '16px !important',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <TextField
              label='View Name'
              value={editingView.name}
              onChange={(e) => setEditingView({ ...editingView, name: e.target.value })}
              fullWidth
              size='small'
              sx={dialogInputSx}
            />
            <FormControl fullWidth size='small' sx={dialogInputSx}>
              <InputLabel>Covariance Matrix</InputLabel>
              <Select
                label='Covariance Matrix'
                value={editingView.covarianceMatrix}
                onChange={(e) =>
                  setEditingView({
                    ...editingView,
                    covarianceMatrix: e.target.value,
                  })
                }
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: theme.cardBackground,
                      border: `1px solid ${theme.border}`,
                      color: theme.textPrimary,
                    },
                  },
                }}
              >
                {COV_MATRIX_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt} sx={{ color: theme.textPrimary, fontSize: '14px' }}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <TextField
                label='VaR Confidence Level (%)'
                type='number'
                value={editingView.varConfidenceLevel}
                onChange={(e) =>
                  setEditingView({
                    ...editingView,
                    varConfidenceLevel: Number(e.target.value),
                  })
                }
                inputProps={{ min: 0, max: 100, step: 0.5 }}
                size='small'
                sx={dialogInputSx}
              />
              <FormHelperText sx={{ color: theme.textSecondary, mx: 0 }}>
                Enter any value between 0-100 (e.g., 90, 95, 97.5, 99)
              </FormHelperText>
            </FormControl>
            <Box>
              <Typography sx={{ fontSize: '14px', color: theme.textSecondary, mb: '6px' }}>VaR Time Period</Typography>
              <Stack direction='row' spacing={1.5}>
                <TextField
                  type='number'
                  value={editingView.varTimePeriodValue}
                  onChange={(e) =>
                    setEditingView({
                      ...editingView,
                      varTimePeriodValue: Number(e.target.value),
                    })
                  }
                  inputProps={{ min: 1, step: 1 }}
                  size='small'
                  sx={{ ...dialogInputSx, flex: 1 }}
                />
                <FormControl size='small' sx={{ ...dialogInputSx, flex: 2 }}>
                  <InputLabel>Unit</InputLabel>
                  <Select
                    label='Unit'
                    value={editingView.varTimePeriodUnit}
                    onChange={(e) =>
                      setEditingView({
                        ...editingView,
                        varTimePeriodUnit: e.target.value as VaRPeriod,
                      })
                    }
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: theme.cardBackground,
                          border: `1px solid ${theme.border}`,
                          color: theme.textPrimary,
                        },
                      },
                    }}
                  >
                    <MenuItem value='' disabled sx={{ color: theme.textSecondary, fontSize: '14px' }}>
                      Select
                    </MenuItem>
                    {VAR_PERIOD_UNITS.map((u) => (
                      <MenuItem key={u} value={u} sx={{ color: theme.textPrimary, fontSize: '14px' }}>
                        {VarPeriodLookup[u]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Box>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button variant='outlined' onClick={() => setEditDialogOpen(false)} sx={dialogActionsSx}>
            Cancel
          </Button>
          <Button variant='contained' onClick={handleSaveEdit} sx={dialogPrimaryBtn}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default RiskMetricsSummaryCard
